'use client';
import React, { useState, useMemo } from 'react';
import { Plus, X, GitCompare } from 'lucide-react';
import { POLICIES, INSURERS, formatCurrency, formatPremium, type Policy } from '@/lib/policyData';
import PlanSelector from './PlanSelector';
import ComparisonTableClient from './ComparisonTableClient';

const DEFAULT_IDS = ['policy-star-001', 'policy-hdfc-001', 'policy-care-001'];

export default function PolicyComparisonClient() {
  const [selectedIds, setSelectedIds] = useState<string[]>(DEFAULT_IDS);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorSlot, setSelectorSlot] = useState<number>(0);

  const selectedPolicies = useMemo(
    () => selectedIds.map((id) => POLICIES.find((p) => p.id === id)).filter(Boolean) as Policy[],
    [selectedIds]
  );

  const openSelector = (slot: number) => {
    setSelectorSlot(slot);
    setSelectorOpen(true);
  };

  const handleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = [...prev];
      next[selectorSlot] = id;
      return next;
    });
    setSelectorOpen(false);
  };

  const removeSlot = (slot: number) => {
    setSelectedIds((prev) => prev.filter((_, i) => i !== slot));
  };

  const addSlot = () => {
    if (selectedIds.length < 3) {
      setSelectorSlot(selectedIds.length);
      setSelectorOpen(true);
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <GitCompare className="w-5 h-5 text-primary" />
          <p className="section-label">Side-by-Side</p>
        </div>
        <h1 className="text-2xl font-bold text-foreground">Compare Health Insurance Plans</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Select up to 3 plans and compare every feature side by side
        </p>
      </div>

      {/* Plan selector cards row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[0, 1, 2].map((slot) => {
          const policy = selectedPolicies[slot];
          const insurer = policy ? INSURERS[policy.insurer] : null;

          if (policy && insurer) {
            return (
              <div
                key={`slot-${slot}`}
                className="bg-card border-2 border-primary/30 rounded-2xl p-4 relative"
              >
                <button
                  onClick={() => removeSlot(slot)}
                  className="absolute top-3 right-3 p-1 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  aria-label="Remove plan"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-extrabold text-xs ${insurer.bgColor} ${insurer.color}`}>
                    {insurer.abbr}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{insurer.name}</p>
                    <p className="text-sm font-bold text-foreground leading-tight">{policy.planName}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-extrabold text-primary font-tabular">{formatPremium(policy.premium)}</p>
                    <p className="text-xs text-muted-foreground">/ year</p>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-bold text-foreground font-tabular">{formatCurrency(policy.coverageAmount)}</p>
                    <p className="text-xs text-muted-foreground">coverage</p>
                  </div>
                </div>
                <button
                  onClick={() => openSelector(slot)}
                  className="mt-3 w-full text-xs text-primary font-medium py-1.5 rounded-lg border border-primary/20 hover:bg-primary/5 transition-colors"
                >
                  Change Plan
                </button>
              </div>
            );
          }

          if (slot < selectedIds.length) {
            return (
              <div key={`slot-${slot}`} className="bg-card border-2 border-dashed border-border rounded-2xl p-4 flex flex-col items-center justify-center min-h-[140px]">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center mb-2">
                  <Plus className="w-4 h-4 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">Plan not found</p>
              </div>
            );
          }

          if (slot === selectedIds.length && selectedIds.length < 3) {
            return (
              <button
                key={`slot-${slot}`}
                onClick={addSlot}
                className="bg-card border-2 border-dashed border-primary/30 rounded-2xl p-4 flex flex-col items-center justify-center min-h-[140px] hover:border-primary hover:bg-primary/5 transition-all duration-150 group"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-2 group-hover:bg-primary/20 transition-colors">
                  <Plus className="w-5 h-5 text-primary" />
                </div>
                <p className="text-sm font-semibold text-primary">Add Plan to Compare</p>
                <p className="text-xs text-muted-foreground mt-0.5">Click to select</p>
              </button>
            );
          }

          return (
            <div key={`slot-${slot}`} className="bg-muted/30 border-2 border-dashed border-border rounded-2xl p-4 flex flex-col items-center justify-center min-h-[140px] opacity-50">
              <Plus className="w-5 h-5 text-muted-foreground mb-1" />
              <p className="text-xs text-muted-foreground">Add 3rd plan (optional)</p>
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      {selectedPolicies.length >= 1 ? (
        <ComparisonTableClient policies={selectedPolicies} />
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center bg-card border border-border rounded-2xl">
          <GitCompare className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-bold text-foreground mb-2">Select plans to compare</h3>
          <p className="text-sm text-muted-foreground">Choose at least 2 health insurance plans above to see a detailed feature-by-feature comparison.</p>
        </div>
      )}

      {/* Plan Selector Modal */}
      {selectorOpen && (
        <PlanSelector
          onSelect={handleSelect}
          onClose={() => setSelectorOpen(false)}
          excludeIds={selectedIds.filter((_, i) => i !== selectorSlot)}
        />
      )}
    </div>
  );
}